import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { IFlight, IGroundEvent, INonFlyingAssignment, IPositioningFlight } from "@vmosolutions/oc-interfaces";
import { IPairingEditorEventGroup, defaultEventGroup } from "./PairingEventTable";
import _ from "lodash";
import { Box, Theme } from "@mui/material";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { markInteraction, recordFreezeEvent } from "src/utils/freezeMonitor";

interface IFlightDropTargetProps {
  id: string;
  updateEventGroups: any;
  eventGroupTables: (pendingflights?: IFlight[]) => JSX.Element;
}

export const FlightDropTarget = (props: IFlightDropTargetProps): JSX.Element => {
  // Set pending position flight.
  const { id, updateEventGroups, eventGroupTables } = props;
  const dispatch = useDispatch();
  const [pendingFlights, setPendingFlights] = useState<IFlight[]>([]);

  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[id]);
  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() &&
        (draggedItem?.type === "FLIGHT" ||
          draggedItem?.type === "SELECTED_FLIGHTS" ||
          draggedItem?.type === "SELECTED_GROUNDEVENTS" ||
          draggedItem?.type === "GROUND_EVENT" ||
          draggedItem?.type === "POSFLIGHT" ||
          draggedItem?.type === "NONFLIGHT"),
    };
  });

  // HANDLE FLIGHTS if dragged over eventgroups
  const [{ isOver }, drop] = useDrop({
    accept: [
      "FLIGHT",
      "SELECTED_FLIGHTS",
      "SELECTED_GROUNDEVENTS",
      "UNASSIGNED_FLIGHT",
      "POSFLIGHT",
      "NONFLIGHT",
      "GROUND_EVENT",
    ],
    drop(item: any, monitor: DropTargetMonitor) {
      markInteraction("flight dropped on pairing editor", { pairingId: id, itemType: item.type });
      try {
        let newFlights: IFlight[] = [];
        let newGroundEvents: IGroundEvent[] = [];
        let newPosFlights: IPositioningFlight[] = [];
        let newNFAs: INonFlyingAssignment[] = [];
        switch (item.type) {
          case "FLIGHT":
          case "UNASSIGNED_FLIGHT":
            newFlights = [item.flight];
            break;
          case "SELECTED_FLIGHTS":
            newFlights = item.selectedFlights;
            break;
          case "SELECTED_GROUNDEVENTS":
            //left it using selectedFlights because I wasn't sure if changing the timeine
            //drag layer would cause issues else where in the app
            newGroundEvents = item.selectedFlights;
            break;
          case "GROUND_EVENT":
            //lets make sure this event is allowed on the pairing
            if (
              item?.groundEvent.eventType?.isGroundTurnBack ||
              item?.groundEvent.eventType?.isCrewable ||
              item?.groundEvent.eventType?.isSim
            ) {
              newGroundEvents = [item.groundEvent];
            }
            break;
          case "POSFLIGHT":
            newPosFlights = [item.flight];
            break;
          case "NONFLIGHT":
            newNFAs = [item.flight];
            break;
          default:
          // code block
        }
        if (newFlights.length > 0) {
          const newEventGroup: IPairingEditorEventGroup = {
            ...defaultEventGroup,
            flights: newFlights,
          };
          const eventGroups = storedPairing.eventGroups;
          const UpdatedEventGroups: IPairingEditorEventGroup[] = [...eventGroups, newEventGroup];
          updateEventGroups(UpdatedEventGroups);
        } else if (newGroundEvents?.length > 0) {
          //Don't re-add this into the pairing editor if it already exists on thie pairing
          const eventGroups = storedPairing.eventGroups;
          const existingGEs = eventGroups.flatMap((eg) => eg.groundEvents);
          const filteredGEs = _.cloneDeep(newGroundEvents).filter(
            (ge) => !existingGEs.some((dge) => dge._id === ge._id)
          );
          if (filteredGEs.length > 0) {
            const newEventGroup: IPairingEditorEventGroup = {
              ...defaultEventGroup,
              groundEvents: filteredGEs,
            };
            const eventGroups = storedPairing.eventGroups;
            const UpdatedEventGroups: IPairingEditorEventGroup[] = [...eventGroups, newEventGroup];
            updateEventGroups(UpdatedEventGroups);
          }
        } else if (newNFAs?.length > 0) {
          const eventGroups = storedPairing.eventGroups;
          const existingNFAs = eventGroups.flatMap((eg) => eg.nonFlyingAssignments);
          //Don't re-add this into the pairing editor if it already exists on thie pairing
          const filteredNFAs = _.cloneDeep(newNFAs).filter((nfa) => !existingNFAs.some((dnfa) => dnfa._id === nfa._id));
          if (filteredNFAs.length > 0) {
            filteredNFAs.forEach((nfa) => delete nfa._id);
            const newEventGroup: IPairingEditorEventGroup = {
              ...defaultEventGroup,
              nonFlyingAssignments: filteredNFAs,
            };
            const eventGroups = storedPairing.eventGroups;
            const UpdatedEventGroups: IPairingEditorEventGroup[] = [...eventGroups, newEventGroup];
            updateEventGroups(UpdatedEventGroups);
          }
        } else if (newPosFlights?.length > 0) {
          const eventGroups = storedPairing.eventGroups;
          const existingPosFlights = eventGroups.flatMap((eg) => eg.positioningFlights);
          //if this pos flight already exists in the editor, don't add it again (check on _id and flight_id)
          const filteredPosFlts = _.cloneDeep(newPosFlights).filter(
            (pos) =>
              !(
                existingPosFlights.some((dpos) => dpos._id === pos._id) ||
                (!!pos.flight_id && existingPosFlights.some((dpos) => dpos.flight_id === pos.flight_id))
              )
          );
          if (filteredPosFlts.length > 0) {
            filteredPosFlts.forEach((posFlt) => {
              delete posFlt._id;
              delete posFlt.bookings;
            });
            const newEventGroup: IPairingEditorEventGroup = {
              ...defaultEventGroup,
              positioningFlights: filteredPosFlts,
            };
            const UpdatedEventGroups: IPairingEditorEventGroup[] = [...eventGroups, newEventGroup];
            updateEventGroups(UpdatedEventGroups);
          }
        }
      } catch (error) {
        // react-dnd calls drop() from a native listener, so no error boundary sees this; log it and tell the user.
        recordFreezeEvent("drop failed on pairing editor", {
          pairingId: id,
          itemType: item?.type,
          message: error instanceof Error ? error.message : JSON.stringify(error),
        });
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: null,
            variant: "error",
            message: "Error: Unable to add the dropped item to the pairing.",
          })
        );
      }
    },
    hover(item: any) {
      const hovered: IFlight[] =
        item.type === "SELECTED_FLIGHTS"
          ? item.selectedFlights
          : item.type === "FLIGHT" || item.type === "UNASSIGNED_FLIGHT"
            ? [item.flight]
            : [];
      // Hover fires on every dragover, so the event table only re-renders when the hovered flights change.
      setPendingFlights((pending) =>
        pending.length === hovered.length && pending.every((flight, i) => flight?._id === hovered[i]?._id)
          ? pending
          : hovered
      );
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  useEffect(() => {
    if (isOver) {
      markInteraction("drag over pairing editor", { pairingId: id });
    } else {
      setPendingFlights([]);
    }
  }, [isOver, id]);

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={` ${isValidDraggable && "active"} ${isOver && "over"}`}
      ref={drop}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        To add flights to this pairing, drag/drop them into this zone from the timeline or advanced search menu.
      </div>
      {eventGroupTables(pendingFlights)}
    </Box>
  );
};
